import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Crown, Check, ArrowRight, ShieldCheck, Sparkles, Image, FileText,
  UploadCloud, Heart, Search, MessageSquareQuote, Wand2, Wallet, Gift, Loader2, Palette,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ICONS = {
  verify: ShieldCheck, membership: Crown, tiers: Sparkles, photo: Image,
  bio: FileText, skin: Palette, post: UploadCloud, interests: Heart, looking_for: Search,
  prompts: MessageSquareQuote, avatar: Wand2, payouts: Wallet, referral: Gift,
};

export default function CreatorOnboarding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get("/creator/onboarding").then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const go = (route) => navigate(route);

  if (!data) {
    return (
      <div data-testid="onboarding-loading" className="min-h-screen flex items-center justify-center text-[#D4AF37]">
        <Loader2 className="animate-spin" size={28} />
      </div>
    );
  }

  const pct = data.percent;
  const R = 52, C = 2 * Math.PI * R;
  const complete = pct >= 100;

  return (
    <div data-testid="creator-onboarding" className="max-w-3xl mx-auto px-5 md:px-8 py-10">
      <div className="overline">Creator Studio</div>
      <h1 className="font-heading text-4xl sm:text-5xl gold-text leading-tight mt-1">
        Your launch checklist
      </h1>
      <p className="text-[#A0A0A0] mt-2 text-sm md:text-base max-w-xl">
        Set up a fun, full profile and turn on everything you need to start earning in the Empire.
      </p>

      {/* Progress card */}
      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="mt-8 rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#1A1508] to-[#0A0A0A] p-6 flex items-center gap-6"
        data-testid="onboarding-progress"
      >
        <div className="relative flex-shrink-0" style={{ width: 128, height: 128 }}>
          <svg width="128" height="128" className="-rotate-90">
            <circle cx="64" cy="64" r={R} fill="none" stroke="#2A2A2A" strokeWidth="10" />
            <motion.circle
              cx="64" cy="64" r={R} fill="none" stroke="#D4AF37" strokeWidth="10"
              strokeLinecap="round" strokeDasharray={C}
              initial={{ strokeDashoffset: C }}
              animate={{ strokeDashoffset: C - (C * pct) / 100 }}
              transition={{ duration: 0.9, ease: "easeOut" }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span data-testid="onboarding-percent" className="font-heading text-3xl gold-text">{pct}%</span>
            <span className="text-[10px] text-[#A0A0A0] uppercase tracking-wider">setup</span>
          </div>
        </div>
        <div className="flex-1">
          <div className="font-heading text-xl text-white">
            {complete ? "You're all set, superstar 👑" : `${data.required_done} of ${data.required_total} essentials done`}
          </div>
          <p className="text-sm text-[#A0A0A0] mt-1">
            {complete
              ? "Your profile is live and ready to earn. Keep posting to grow your fans."
              : "Finish the essentials to make your profile go live and start earning."}
          </p>
        </div>
      </motion.div>

      {/* Steps */}
      <div className="mt-8 space-y-3">
        {data.steps.map((s, i) => {
          const Icon = ICONS[s.key] || Sparkles;
          return (
            <motion.div
              key={s.key}
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              data-testid={`onboarding-step-${s.key}`}
              className={`rounded-xl border p-4 flex items-center gap-4 transition-colors ${
                s.done ? "border-[#D4AF37]/25 bg-[#D4AF37]/[0.04]" : "border-[#2A2A2A] bg-[#111]"
              }`}
            >
              <div className={`flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center ${
                s.done ? "bg-[#D4AF37] text-black" : "bg-[#1A1A1A] text-[#D4AF37]"
              }`}>
                {s.done ? <Check size={22} /> : <Icon size={20} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`font-medium ${s.done ? "text-white" : "text-white"}`}>{s.title}</span>
                  {!s.required && (
                    <span className="text-[10px] uppercase tracking-wider text-[#A0A0A0] border border-[#2A2A2A] rounded px-1.5 py-0.5">
                      Optional
                    </span>
                  )}
                </div>
                <p className="text-xs text-[#A0A0A0] mt-0.5">{s.description}</p>
              </div>
              {s.done ? (
                <span data-testid={`onboarding-done-${s.key}`} className="text-xs font-medium text-[#D4AF37] flex-shrink-0">Done</span>
              ) : (
                <button
                  data-testid={`onboarding-cta-${s.key}`}
                  onClick={() => go(s.route)}
                  className="flex-shrink-0 gold-gradient text-black text-sm font-medium px-4 py-2 rounded-lg flex items-center gap-1.5 hover:opacity-90 transition-opacity"
                >
                  {s.cta} <ArrowRight size={15} />
                </button>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
